import React from "react";
import styled from "styled-components";
import moment from "moment";
import { COLORS } from "../../../assets/colors";
import NoData from "../../NoData";

const tableHeaders = [
  "Route",
  "Preferred Date",
  "Seats",
  "Contact",
  "Note",
  "Status",
  "Submitted",
  "Actions",
];

const RideWishesTable = ({ wishes, onUpdateStatus, onDelete }) => {
  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <tr>
            {tableHeaders.map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {wishes?.length === 0 && (
            <tr>
              <td colSpan={tableHeaders.length}>
                <NoData
                  showImage={true}
                  message="No ride wishes found"
                  button={false}
                />
              </td>
            </tr>
          )}
          {wishes?.map((wish, index) => (
            <TableRow key={wish.id || index}>
              <TableCell>
                <div className="route-info">
                  <div className="location">
                    {wish.leaving_from} &rarr; {wish.heading_to}
                  </div>
                </div>
              </TableCell>

              <TableCell>
                <div className="date-info">
                  {wish.preferred_date
                    ? moment(wish.preferred_date).format("MMM Do, YYYY")
                    : "Flexible"}
                </div>
              </TableCell>

              <TableCell>
                <div className="bold">{wish.seats}</div>
              </TableCell>

              <TableCell>
                <div className="contact-info">
                  {wish.requestedBy ? (
                    <div className="user-info">
                      <span className="name">
                        {wish.requestedBy.first_name}{" "}
                        {wish.requestedBy.last_name}
                      </span>
                      <span className="phone">
                        {wish.requestedBy.mobile_number || "N/A"}
                      </span>
                    </div>
                  ) : (
                    <div className="guest-info">
                      <span className="name">
                        {wish.contact_name || "Guest"}
                      </span>
                      <span className="phone">
                        {wish.contact_phone || "N/A"}
                      </span>
                    </div>
                  )}
                </div>
              </TableCell>

              <TableCell>
                <div className="note-text">
                  {wish.note ? wish.note.substring(0, 50) + (wish.note.length > 50 ? "..." : "") : "-"}
                </div>
              </TableCell>

              <TableCell>
                <StatusBadge status={wish.status}>{wish.status}</StatusBadge>
              </TableCell>

              <TableCell>
                <div className="date-info">
                  {moment(wish.createdAt).format("MMM Do, hh:mm A")}
                </div>
              </TableCell>

              <TableCell>
                <ActionButtons>
                  {wish.status === "pending" && (
                    <ActionBtn
                      className="fulfill"
                      onClick={() => onUpdateStatus(wish.id, "fulfilled")}
                    >
                      ✓ Fulfill
                    </ActionBtn>
                  )}
                  {wish.status === "pending" && (
                    <ActionBtn
                      className="expire"
                      onClick={() => onUpdateStatus(wish.id, "expired")}
                    >
                      ✗ Expire
                    </ActionBtn>
                  )}
                  <ActionBtn
                    className="delete"
                    onClick={() => onDelete(wish.id)}
                  >
                    🗑
                  </ActionBtn>
                </ActionButtons>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </StyledTable>
    </TableContainer>
  );
};

export default RideWishesTable;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    background-color: #054752;
    padding: 0.75rem 1rem;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 500;
    text-align: left;
    white-space: nowrap;
  }
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f8fafc;
  }

  &:hover {
    background-color: #f1f5f9;
  }
`;

const TableCell = styled.td`
  padding: 0.875rem 1rem;
  color: ${COLORS.adminTextPrimary};
  font-size: 0.875rem;
  vertical-align: middle;

  .route-info {
    .location {
      font-weight: 600;
      color: ${COLORS.adminTextPrimary};
    }
  }

  .date-info {
    color: ${COLORS.adminTextSecondary};
    font-size: 0.8rem;
  }

  .bold {
    font-weight: 600;
  }

  .contact-info {
    .user-info,
    .guest-info {
      display: flex;
      flex-direction: column;
      gap: 0.2rem;

      .name {
        font-weight: 600;
        font-size: 0.85rem;
      }
      .phone {
        color: ${COLORS.adminTextSecondary};
        font-size: 0.8rem;
      }
    }
  }

  .note-text {
    color: ${COLORS.adminTextSecondary};
    font-size: 0.8rem;
    max-width: 150px;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  text-transform: capitalize;

  background: ${(props) => {
    switch (props.status) {
      case "pending":
        return "rgba(245, 158, 11, 0.1)";
      case "fulfilled":
        return "rgba(16, 185, 129, 0.1)";
      case "expired":
        return "rgba(239, 68, 68, 0.1)";
      default:
        return "rgba(100, 116, 139, 0.1)";
    }
  }};

  color: ${(props) => {
    switch (props.status) {
      case "pending":
        return COLORS.adminWarning;
      case "fulfilled":
        return COLORS.adminSuccess;
      case "expired":
        return COLORS.adminError;
      default:
        return COLORS.adminTextSecondary;
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionBtn = styled.button`
  padding: 0.3rem 0.6rem;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &.fulfill {
    background: rgba(16, 185, 129, 0.1);
    color: ${COLORS.adminSuccess};
    &:hover {
      background: rgba(16, 185, 129, 0.2);
    }
  }

  &.expire {
    background: rgba(245, 158, 11, 0.1);
    color: ${COLORS.adminWarning};
    &:hover {
      background: rgba(245, 158, 11, 0.2);
    }
  }

  &.delete {
    background: rgba(239, 68, 68, 0.1);
    color: ${COLORS.adminError};
    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  }
`;
